import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import './HRManualAttendance.css';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

function toLocalDateStr(date) {
  return date.toISOString().split('T')[0];
}

function formatTimeForInput(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length-1][0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

const STATUS_OPTIONS = [
  { value: 'present',        label: '✓ Present' },
  { value: 'minor_late',     label: '⏱ Minor Late' },
  { value: 'major_late',     label: '⏱ Major Late' },
  { value: 'half_day',       label: '½ Half Day' },
  { value: 'absent',         label: '✗ Absent' },
  { value: 'approved_leave', label: '📋 Approved Leave' },
  { value: 'casual_auto',    label: '📋 Casual Leave' },
  { value: 'public_holiday', label: '🎉 Public Holiday' },
];

// ── Edit Modal ────────────────────────────────────────────
function EditModal({ employee, record, date, onSave, onClose }) {
  const [clockIn,  setClockIn]  = useState(record ? formatTimeForInput(record.clock_in)  : '');
  const [clockOut, setClockOut] = useState(record ? formatTimeForInput(record.clock_out) : '');
  const [status,   setStatus]   = useState(record?.status || 'present');
  const [notes,    setNotes]    = useState(record?.notes  || '');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      // Build datetime strings combining date + time input
      const buildDT = (timeStr) => {
        if (!timeStr) return null;
        return `${date}T${timeStr}:00+05:00`; // PKT
      };

      const clockInDT  = buildDT(clockIn);
      const clockOutDT = buildDT(clockOut);

      let res;
      if (record?.id) {
        // Update existing record
        res = await fetch(`${BASE}/attendance/${record.id}/override`, {
          method: 'PUT', headers: authHeaders(),
          body: JSON.stringify({
            clock_in:  clockInDT,
            clock_out: clockOutDT,
            status, notes,
          }),
        });
      } else {
        // Create new record
        res = await fetch(`${BASE}/attendance/manual`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({
            employee_id: employee.id,
            date,
            clock_in:  clockInDT,
            clock_out: clockOutDT,
            status, notes,
          }),
        });
      }

      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }
      onSave();
    } catch { setError('Server error — try again'); }
    finally { setSaving(false); }
  };

  return (
    <div className="ma-modal-overlay" onClick={onClose}>
      <div className="ma-modal" onClick={e => e.stopPropagation()}>
        <div className="ma-modal-header">
          <div className="ma-modal-emp">
            <div className="ma-modal-av" style={{ background: getAvatarColor(employee.full_name) }}>
              {getInitials(employee.full_name)}
            </div>
            <div>
              <div className="ma-modal-name">{employee.full_name}</div>
              <div className="ma-modal-meta">{employee.employee_id} · {date}</div>
            </div>
          </div>
          <button className="ma-modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="ma-modal-error">{error}</div>}

        <div className="ma-modal-body">
          <div className="ma-field">
            <label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="ma-field-row">
            <div className="ma-field">
              <label>Clock In Time</label>
              <input type="time" value={clockIn}
                onChange={e => setClockIn(e.target.value)} />
            </div>
            <div className="ma-field">
              <label>Clock Out Time</label>
              <input type="time" value={clockOut}
                onChange={e => setClockOut(e.target.value)} />
            </div>
          </div>
          <div className="ma-field">
            <label>Notes (reason for manual entry)</label>
            <textarea rows={2} placeholder="e.g. Employee reported in but system issue"
              value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="ma-modal-footer">
          <button className="ma-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="ma-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : record?.id ? 'Update Record' : 'Add Record'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────
export default function HRManualAttendance() {
  const [date, setDate]           = useState(toLocalDateStr(new Date()));
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [editData, setEditData]   = useState(null); // { employee, record }
  const [search, setSearch]       = useState('');
  const [success, setSuccess]     = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [empRes, attRes] = await Promise.all([
        api.getAllEmployees(),
        fetch(`${BASE}/attendance/?date=${date}`, { headers: authHeaders() }).then(r => r.json()),
      ]);
      setEmployees((empRes.employees || []).filter(e => e.role === 'employee'));
      setAttendance(attRes.records || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getRecord = (emp) => attendance.find(r => r.emp_code === emp.employee_id);

  const getStatusBadge = (rec) => {
    if (!rec) return { label: 'No Record', cls: 'ma-s-none' };
    const s = rec.status;
    if (s === 'present')        return { label: 'Present', cls: 'ma-s-present' };
    if (s === 'minor_late' || s === 'major_late' || s === 'half_day')
                                return { label: `Late ${rec.late_minutes}m`, cls: 'ma-s-late' };
    if (s === 'absent')         return { label: 'Absent', cls: 'ma-s-absent' };
    if (s === 'approved_leave') return { label: 'Approved Leave', cls: 'ma-s-leave' };
    if (s === 'casual_auto')    return { label: 'Casual Leave', cls: 'ma-s-leave' };
    if (s === 'public_holiday') return { label: 'Holiday', cls: 'ma-s-holiday' };
    if (rec.clock_in)           return { label: 'Present', cls: 'ma-s-present' };
    return { label: 'No Record', cls: 'ma-s-none' };
  };

  const handleSave = () => {
    setEditData(null);
    setSuccess('Attendance updated successfully');
    setTimeout(() => setSuccess(''), 3000);
    fetchData();
  };

  const filtered = employees.filter(e =>
    e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.employee_id?.toLowerCase().includes(search.toLowerCase())
  );

  const presentCount = attendance.filter(r => r.clock_in).length;
  const absentCount  = employees.filter(e => !getRecord(e)).length;

  return (
    <div className="ma-root">
      <div className="ma-header">
        <div>
          <h1 className="ma-title">⚠️ Manual Attendance</h1>
          <p className="ma-subtitle">Temporary feature — transition period only</p>
        </div>
        <div className="ma-header-right">
          <input type="date" className="ma-date-input"
            value={date} onChange={e => setDate(e.target.value)}
            max={toLocalDateStr(new Date())} />
        </div>
      </div>

      {/* Warning banner */}
      <div className="ma-warning-banner">
        ⚠️ Manual entries override automated tracking. Use only when necessary and always add a note explaining why.
      </div>

      {success && <div className="ma-success">{success}</div>}

      {/* Stats */}
      <div className="ma-stats">
        <div className="ma-stat ma-stat-green">
          <span className="ma-stat-num">{presentCount}</span>
          <span className="ma-stat-lbl">Present</span>
        </div>
        <div className="ma-stat ma-stat-red">
          <span className="ma-stat-num">{absentCount}</span>
          <span className="ma-stat-lbl">No Record</span>
        </div>
        <div className="ma-stat">
          <span className="ma-stat-num">{employees.length}</span>
          <span className="ma-stat-lbl">Total</span>
        </div>
      </div>

      {/* Search */}
      <input className="ma-search" type="text"
        placeholder="Search employee name or ID…"
        value={search} onChange={e => setSearch(e.target.value)} />

      {/* Employee list */}
      {loading ? (
        <div className="ma-loading">Loading…</div>
      ) : (
        <div className="ma-list">
          {filtered.map((emp, i) => {
            const rec    = getRecord(emp);
            const badge  = getStatusBadge(rec);
            return (
              <div key={i} className="ma-row">
                <div className="ma-emp-cell">
                  <div className="ma-av" style={{ background: getAvatarColor(emp.full_name) }}>
                    {getInitials(emp.full_name)}
                  </div>
                  <div className="ma-emp-info">
                    <div className="ma-emp-name">{emp.full_name}</div>
                    <div className="ma-emp-meta">{emp.employee_id} · {emp.location_name || '—'}</div>
                  </div>
                </div>

                <div className="ma-times">
                  {rec?.clock_in ? (
                    <>
                      <span className="ma-time-in">IN {formatTimeForInput(rec.clock_in)}</span>
                      {rec.clock_out && <span className="ma-time-out">OUT {formatTimeForInput(rec.clock_out)}</span>}
                    </>
                  ) : (
                    <span className="ma-no-time">—</span>
                  )}
                </div>

                <span className={`ma-status-badge ${badge.cls}`}>{badge.label}</span>

                <button className="ma-edit-btn"
                  onClick={() => setEditData({ employee: emp, record: rec || null })}>
                  {rec ? 'Edit' : '+ Add'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editData && (
        <EditModal
          employee={editData.employee}
          record={editData.record}
          date={date}
          onSave={handleSave}
          onClose={() => setEditData(null)}
        />
      )}
    </div>
  );
}